/**
 * REV-878: where an invitation token waits while its recipient signs in.
 *
 * AcceptInvitationPage strips the token from the URL on arrival, so it is not
 * in any location a sign-in link carries, and LoginPage returns to a pathname
 * alone. sessionStorage is the one place it survives that round trip without
 * going back into a URL: per tab, and gone when the tab closes.
 *
 * It is also bounded in time and by session. An entry records when it was
 * parked and is ignored (and removed) after PENDING_INVITATION_TTL_MS, and the
 * auth store's logout removes it, so a token left behind by a recipient who
 * wandered off does not sit in the tab for whoever signs in next.
 */

export const PENDING_INVITATION_TOKEN_KEY = 'revenant.pendingInvitationToken';

/** How long a parked token is handed back: long enough to sign in or register. */
export const PENDING_INVITATION_TTL_MS = 30 * 60 * 1000;

interface ParkedToken {
  token: string;
  storedAt: number;
}

function isParkedToken(value: unknown): value is ParkedToken {
  const v = value as Partial<ParkedToken> | null;
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof v.token === 'string' &&
    v.token !== '' &&
    typeof v.storedAt === 'number' &&
    Number.isFinite(v.storedAt)
  );
}

/** Remove the parked token, if any. */
export function clearPendingInvitationToken(): void {
  try {
    sessionStorage.removeItem(PENDING_INVITATION_TOKEN_KEY);
  } catch {
    // sessionStorage can throw in private-mode/sandboxed contexts.
  }
}

/**
 * Park a token for the sign-in round trip.
 *
 * @param token the invitation token; nothing is stored when it is empty
 * @param now the time to record, for tests
 */
export function stashPendingInvitationToken(token: string | null, now = Date.now()): void {
  if (!token) return;
  try {
    sessionStorage.setItem(
      PENDING_INVITATION_TOKEN_KEY,
      JSON.stringify({ token, storedAt: now } satisfies ParkedToken)
    );
  } catch {
    // As above: without storage the recipient opens the email link again.
  }
}

/**
 * The parked token, if one is there and still fresh. Does not remove a fresh
 * one — StrictMode runs an initializer twice — but removes an expired or
 * unreadable entry, which is of no use to anyone.
 *
 * @param now the current time, for tests
 * @returns the token, or null
 */
export function readPendingInvitationToken(now = Date.now()): string | null {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(PENDING_INVITATION_TOKEN_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    parsed = null;
  }
  if (
    !isParkedToken(parsed) ||
    now - parsed.storedAt > PENDING_INVITATION_TTL_MS ||
    parsed.storedAt > now
  ) {
    clearPendingInvitationToken();
    return null;
  }
  return parsed.token;
}
